<template>
  <div>
    <v-row class="px-6 mb-4">
      <v-col cols="4">
        <v-text-field
          v-model="searchInputValue"
          prepend-inner-icon="$magnify"
          outlined
          dense
          single-line
          hide-details="auto"
          :placeholder="$t('spedilink.offers.searchPlaceholder')"
          clearable
        />
      </v-col>
    </v-row>

    <v-data-table
      :items="offers"
      :loading="loading"
      :headers="headers"
      hide-default-footer
      :page="dataTableOptions.page"
      :items-per-page="dataTableOptions.itemsPerPage"
      @update:options="updateOptions"
    >
      <template v-slot:[`item.name`]="{ item }">
        <span v-if="item.isPublicLink">{{ $t('spedilink.links.publicLink') }}</span>
        <span v-else>{{ item.name }}</span>
      </template>

      <template v-slot:[`item.description`]="{ item }">
        <span v-if="item.isPublicLink">—</span>
        <span v-else>{{ item.description }}</span>
      </template>

      <template v-slot:[`item.companyName`]="{ item }">
        <span v-if="item.isPublicLink">—</span>
        <span v-else>{{ item.companyName }}</span>
      </template>

      <template v-slot:[`item.owner`]="{ item }">
        <span v-if="item.isPublicLink">—</span>
        <span v-else-if="item.ownerName || item.ownerSurname">{{ item.ownerName }} {{ item.ownerSurname }}</span>
        <span v-else>—</span>
      </template>

      <template v-slot:[`item.createdAt`]="{ item }">
        <span v-if="item.id === GUID_EMPTY || item.isPublicLink">—</span>
        <span v-else>{{ item.createdAt | dateFormat }}</span>
      </template>

      <template v-slot:[`item.open`]="{ item }">
        <div class="d-flex align-center">
          <v-btn
            small
            color="primary"
            depressed
            @click="openOffer(item)"
          >
            {{ $t('spedilink.offers.open') }}
          </v-btn>
          <v-btn
            v-if="!item.isPublicLink"
            icon
            class="ml-2"
            :disabled="isBellDisabled(item)"
            @click="onBellClick(item)"
          >
            <v-badge
              :content="getBellMetaForDisplay(item).count"
              :value="getBellMetaForDisplay(item).count > 0"
              :color="getBellMetaForDisplay(item).hasNew ? 'error' : 'primary'"
              overlap
            >
              <v-icon :color="getBellMetaForDisplay(item).hasNew ? 'error' : 'grey lighten-1'">$infoNewOffers</v-icon>
            </v-badge>
          </v-btn>
          <v-tooltip v-if="isGuest" bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                class="ml-1"
                v-bind="attrs"
                v-on="on"
                @click.stop="openMyInterestsGuest(item)"
              >
                <v-icon color="primary" size="20">
                  $interestCount
                </v-icon>
              </v-btn>
            </template>
            <span>{{ $t('spedilink.offers.myInterests') }}</span>
          </v-tooltip>
        </div>
      </template>

      <template v-slot:[`item.actions`]="{ item }">
        <v-menu
          v-if="item.id !== GUID_EMPTY"
          bottom
          left
        >
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon v-bind="attrs" v-on="on">
              <v-icon>mdi-dots-vertical</v-icon>
            </v-btn>
          </template>
          <v-list>
            <v-list-item link @click="openUnsubscribeDialog(item)">
              <v-list-item-title class="error--text">{{ $t('spedilink.offers.unsubscribe') }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </template>

      <template v-slot:footer v-if="pageCount > 1">
        <v-pagination v-model="dataTableOptions.page" :length="pageCount" />
      </template>
    </v-data-table>

    <UnsubscribeFromListDialog
      v-model="unsubscribeDialog"
      :offer="selectedOffer"
      @close="unsubscribeDialog = false"
      @unsubscribed="onUnsubscribed()"
    />

    <v-dialog v-model="interestsDialog" max-width="900" scrollable>
      <v-card>
        <v-card-title>{{ $t('offerDetails.interests') }}</v-card-title>
        <v-card-text>
          <SpedilinkInterestsPanel :interests="selectedInterests" :show-archive-actions="true" @interest-archived="onInterestArchived" />
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn color="primary" depressed @click="interestsDialog = false">
            {{ $t('common.close') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <MySpediLinkInterestsDialog
      v-model="myInterestsGuestDialog"
      :link-id="myInterestsGuestLinkId"
    />
  </div>
</template>

<script>
import Service from '@/services'
import UnsubscribeFromListDialog from '@/views/Spedilink/dialogs/UnsubscribeFromListDialog'
import SpedilinkInterestsPanel from '@/views/Market/dialogs/panels/SpedilinkInterestsPanel'
import MySpediLinkInterestsDialog from '@/views/Spedilink/dialogs/MySpediLinkInterestsDialog'
import { GUID_EMPTY, SPEDILINK_LINK_TYPES, SPEDILINK_ROUTE_NAMES } from '@/views/Spedilink/spedilink-constants'

export default {
  name: 'SpedilinkOffersTable',
  components: {
    UnsubscribeFromListDialog,
    SpedilinkInterestsPanel,
    MySpediLinkInterestsDialog
  },
  props: {
    type: {
      type: Number,
      required: true
    }
  },
  data () {
    return {
      GUID_EMPTY,
      offers: [],
      loading: false,
      pageCount: 1,
      searchInputValue: '',
      searchTimeout: null,
      dataTableOptions: {
        page: 1,
        itemsPerPage: 15
      },
      sortBy: null,
      sortDescending: false,
      selectedOffer: {},
      unsubscribeDialog: false,
      interestsDialog: false,
      myInterestsGuestDialog: false,
      myInterestsGuestLinkId: null,
      pendingMarkAllInterestsAsRead: false,
      selectedInterests: [],
      currentLinkForInterests: null,
      headers: [
        { text: this.$t('spedilink.offers.name'), value: 'name' },
        { text: this.$t('spedilink.offers.description'), value: 'description' },
        { text: this.$t('spedilink.offers.company'), value: 'companyName' },
        { text: this.$t('spedilink.offers.owner'), value: 'owner', sortable: false },
        { text: this.$t('spedilink.offers.createdAt'), value: 'createdAt' },
        { text: '', value: 'open', sortable: false },
        { text: '', align: 'end', width: '50', value: 'actions', sortable: false }
      ]
    }
  },
  async created () {
    await this.loadOffers()
  },
  watch: {
    searchInputValue () {
      this.dataTableOptions.page = 1
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.loadOffers()
      }, 500)
    },
    'dataTableOptions.page' () {
      this.loadOffers()
    },
    interestsDialog (newVal, oldVal) {
      if (oldVal && !newVal) {
        this.onInterestsDialogClosed()
      }
    },
    spedilinkEventVersion () {
      this.onRealtimeEvent()
    }
  },
  computed: {
    isGuest () {
      return this.$store.getters.isGuest
    },
    hasSpedilinkModule () {
      return this.$permission('SpedilinkAccess')
    },
    useNewOffersBellMode () {
      return true
    },
    spedilinkEventVersion () {
      return this.$store.getters['spedilink/eventVersion']
    },
    spedilinkLastEvent () {
      return this.$store.getters['spedilink/lastEvent']
    }
  },
  methods: {
    async onRealtimeEvent () {
      const event = this.spedilinkLastEvent
      if (!event) return

      const matchesType = () => {
        if (!event.orderType) return true
        if (this.type === SPEDILINK_LINK_TYPES.FREIGHT) return event.orderType === 'freight'
        return event.orderType === 'vehicle'
      }

      if (event.eventName === 'ListAdded' || event.eventName === 'ListRemoved') {
        const eventType = event.type || event.payload?.type || event.payload?.Type
        if (!eventType || Number(eventType) === this.type) {
          await this.loadOffers()
        }
        return
      }

      if (event.eventName === 'NewInterest') {
        // InterestCount is provided by backend; refresh list to update bell badge.
        await this.loadOffers()
        return
      }

      if (event.eventName === 'NewOffersRead') {
        await this.loadOffers()
        return
      }

      if (['LinkOrderAdded', 'LinkOrderModified', 'LinkOrderRemoved'].includes(event.eventName) && matchesType()) {
        // Backend currently does not provide a compact row-delta payload for this table.
        // Keep safe refresh strategy until final SignalR payload contract is delivered.
        await this.loadOffers()
      }
    },
    async loadOffers () {
      this.loading = true
      try {
        const params = {
          type: this.type,
          page: this.dataTableOptions.page,
          pageSize: this.dataTableOptions.itemsPerPage
        }
        if (this.searchInputValue) {
          params.search = this.searchInputValue
        }
        if (this.sortBy) {
          params.sortBy = this.sortBy
          params.sortDescending = this.sortDescending
        }
        const { data } = await Service.get('spedilink/SpediLinkOffers/GetList', params)
        this.offers = data.results
        this.pageCount = Math.ceil(data.count / this.dataTableOptions.itemsPerPage)
      } catch (e) {
      } finally {
        this.loading = false
      }
    },
    updateOptions (options) {
      if (options.sortBy.length > 0) {
        this.sortBy = options.sortBy[0]
        this.sortDescending = options.sortDesc[0] || false
      } else {
        this.sortBy = null
        this.sortDescending = false
      }
    },
    openOffer (item) {
      this.onBellClick(item)

      const routeName = this.type === SPEDILINK_LINK_TYPES.FREIGHT
        ? SPEDILINK_ROUTE_NAMES.CARGO_OFFER_LIST
        : SPEDILINK_ROUTE_NAMES.VEHICLE_OFFER_LIST
      const resolvedName = item.isPublicLink
        ? this.$t('spedilink.links.publicLink')
        : (item.name ?? item.Name)
      const route = { name: routeName, params: { id: item.id } }
      if (resolvedName != null && String(resolvedName).trim() !== '') {
        route.query = { linkName: String(resolvedName) }
      }
      if (item.isPublicLink) {
        route.query = { ...(route.query || {}), isPublicLink: '1' }
      }
      const href = this.$router.resolve(route).href
      window.open(href, '_blank')
    },
    openUnsubscribeDialog (item) {
      this.selectedOffer = item
      this.unsubscribeDialog = true
    },
    async onUnsubscribed () {
      this.unsubscribeDialog = false
      await this.loadOffers()
    },
    getInterestsMeta (link) {
      // Prefer backend counter from SpediLinkVm, fallback to legacy fields.
      const raw = link.interests || link.Interests
      const totalFromArray = Array.isArray(raw) ? raw.length : 0
      const newFromArray = Array.isArray(raw) ? raw.filter(i => i.isNew || i.IsNew).length : 0

      const total = link.interestCount ?? link.InterestCount ?? link.interestsCount ?? link.InterestsCount ?? link.interestsCounter ?? totalFromArray
      const newTotal = link.newInterestsCount ?? link.NewInterestsCount ?? link.newInterestCount ?? newFromArray

      return {
        total: total || 0,
        newTotal: newTotal || 0
      }
    },
    getInterestsMetaForDisplay (link) {
      return this.getInterestsMeta(link)
    },
    getNewOffersMeta (link) {
      const count =
        link?.newOffersCounter ??
        link?.NewOffersCounter ??
        link?.newOfferCounter ??
        link?.NewOfferCounter ??
        0

      return {
        count: Number(count) || 0,
        hasNew: (Number(count) || 0) > 0
      }
    },
    getBellMetaForDisplay (link) {
      if (this.useNewOffersBellMode) {
        return this.getNewOffersMeta(link)
      }

      const interests = this.getInterestsMetaForDisplay(link)
      return {
        count: interests.total || 0,
        hasNew: (interests.newTotal || 0) > 0
      }
    },
    isBellDisabled (link) {
      if (!this.useNewOffersBellMode) {
        return false
      }

      return this.getBellMetaForDisplay(link).count <= 0
    },
    async onBellClick (link) {
      if (this.useNewOffersBellMode) {
        // Keep optimistic UI reset and persist read status on backend.
        this.$set(link, 'newOffersCounter', 0)
        this.$set(link, 'NewOffersCounter', 0)
        try {
          await Service.post('spedilink/spedilinkoffers/marknewoffersasread', {
            linkId: link?.id === GUID_EMPTY ? null : link?.id,
            linkType: this.type === SPEDILINK_LINK_TYPES.FREIGHT
              ? SPEDILINK_LINK_TYPES.FREIGHT
              : SPEDILINK_LINK_TYPES.VEHICLE
          })
          await this.loadOffers()
        } catch (e) {
        }
        return
      }

      this.openInterests(link)
    },
    mapInterest (i, fallbackKey) {
      return {
        id: i?.interestId || i?.InterestId || i?.id || fallbackKey,
        clientName: `${i?.clientFirstName || i?.ClientFirstName || ''} ${i?.clientLastName || i?.ClientLastName || ''}`.trim(),
        clientEmail: i?.clientEmail || i?.ClientEmail || '',
        clientPhone: i?.clientPhone || i?.ClientPhone || '',
        loadingPlace: i?.loadingPlace || i?.LoadingPlace || '',
        unloadingPlace: i?.unloadingPlace || i?.UnloadingPlace || '',
        content: i?.content || i?.Content || '',
        createdAt: i?.createdAt || i?.CreatedAt,
        orderId: i?.orderId || i?.OrderId,
        orderType: i?.orderType || i?.OrderType || '',
        isOrderArchived: i?.isOrderArchived || i?.IsOrderArchived,
        isNew: i?.isNew || i?.IsNew || false
      }
    },
    async openInterests (link) {
      if (this.isGuest) {
        return
      }
      this.interestsDialog = true
      this.currentLinkForInterests = link
      this.selectedInterests = []
      this.pendingMarkAllInterestsAsRead = false
      try {
        const getInterestsParams = link?.id === GUID_EMPTY ? {} : { linkId: link.id }
        const { data } = await Service.get('spedilink/SpediLinks/GetInterests', getInterestsParams)
        const interests = Array.isArray(data) ? data : (data?.results || data?.Results || [])
        this.selectedInterests = interests.map((i, idx) => this.mapInterest(i, `${link.id}-${idx}`))

        const hasNew = interests.some(i => i?.isNew || i?.IsNew)
        if (hasNew) {
          if (link?.id === GUID_EMPTY) {
            this.pendingMarkAllInterestsAsRead = true
          } else {
            await Service.post('spedilink/SpediLinks/MarkInterestsAsRead', {
              linkId: link.id,
              linkType: this.type === SPEDILINK_LINK_TYPES.FREIGHT
                ? SPEDILINK_LINK_TYPES.FREIGHT
                : SPEDILINK_LINK_TYPES.VEHICLE
            })
            await this.loadOffers()
          }
        }
      } catch (e) {
        this.selectedInterests = []
      }
    },
    async onInterestsDialogClosed () {
      if (!this.pendingMarkAllInterestsAsRead) {
        return
      }

      this.pendingMarkAllInterestsAsRead = false
      try {
        await Service.post('spedilink/SpediLinks/MarkInterestsAsRead', {
          linkId: null,
          linkType: this.type === SPEDILINK_LINK_TYPES.FREIGHT
            ? SPEDILINK_LINK_TYPES.FREIGHT
            : SPEDILINK_LINK_TYPES.VEHICLE
        })
        await this.loadOffers()
      } catch (e) {
      }
    },
    openMyInterestsGuest (link) {
      if (!this.isGuest) {
        return
      }
      this.myInterestsGuestLinkId = link?.id && link.id !== GUID_EMPTY ? link.id : null
      this.myInterestsGuestDialog = true
    },
    async onInterestArchived (interest) {
      // Reload interests list from API to reflect all changes for the same order
      if (this.currentLinkForInterests) {
        try {
          const link = this.currentLinkForInterests
          const getInterestsParams = link?.id === GUID_EMPTY ? {} : { linkId: link.id }
          const { data } = await Service.get('spedilink/SpediLinks/GetInterests', getInterestsParams)
          const interests = Array.isArray(data) ? data : (data?.results || data?.Results || [])
          this.selectedInterests = interests.map((i, idx) => this.mapInterest(i, `${link.id}-${idx}`))
        } catch (e) {
          console.error('Error reloading interests:', e)
        }
      }
      await this.loadOffers()
    }
  }
}
</script>
